// Twilio A2P 10DLC compliance — dedicated public pages required by
// the reviewer notes for campaign CM92d84d8f5d3930c628c72bad6e2c7b56.
//
// Each page renders BUSINESS_IDENTITY exactly as registered with Twilio
// and is reachable via a dedicated URL the reviewer can verify:
//
//   /privacy-policy   → PrivacyPolicyPage
//   /terms-of-service → TermsOfServicePage
//   /sms-program      → SmsProgramPage
//   /contact          → ContactPage
//
// /privacy and /terms (legacy URLs) redirect to the canonical URLs via
// <Navigate> in App.js so every existing inbound link still works.

import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Mail, Phone, MapPin, MessageCircleMore, Shield, FileText } from "lucide-react";
import PublicFooter from "../components/PublicFooter";
import { BUSINESS_IDENTITY, SMS_CONSENT_TEXT, SMS_CONSENT_TEXT_VERSION } from "../lib/business_identity";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Card } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import api from "../lib/api";

const PageShell = ({ children, testid }) => (
  <div className="min-h-screen flex flex-col bg-background" data-testid={testid}>
    {/* Lightweight nav */}
    <header className="border-b">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
        <Link to="/" className="font-display text-xl font-semibold tracking-tight">
          {BUSINESS_IDENTITY.tradeName}
        </Link>
        <nav className="hidden sm:flex items-center gap-5 text-sm">
          <Link to="/privacy-policy" className="text-muted-foreground hover:text-foreground">Privacy</Link>
          <Link to="/terms-of-service" className="text-muted-foreground hover:text-foreground">Terms</Link>
          <Link to="/sms-program" className="text-muted-foreground hover:text-foreground">SMS Program</Link>
          <Link to="/contact" className="text-muted-foreground hover:text-foreground">Contact</Link>
          <Link to="/login" className="text-foreground font-medium">Sign in</Link>
        </nav>
      </div>
    </header>
    <main className="flex-1">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10">
        {children}
      </div>
    </main>
    <PublicFooter />
  </div>
);

const updated = "February 22, 2026";
// Privacy Policy was materially updated (2026-07-31); the other
// Twilio-reviewed pages (Terms, SMS Program, Contact) keep using
// `updated` above and must not be affected by this change.
const privacyUpdated = "July 31, 2026";

// ===================================================================
// /privacy-policy
// ===================================================================
export function PrivacyPolicyPage() {
  return (
    <PageShell testid="privacy-policy-page">
      <Badge variant="outline" className="mb-3"><Shield className="w-3 h-3 mr-1"/>Compliance</Badge>
      <h1 className="font-display text-4xl font-semibold mb-1" data-testid="privacy-policy-heading">Privacy Policy</h1>
      <div className="text-sm text-muted-foreground mb-8">Last updated {privacyUpdated}</div>

      <Section title="1. Who we are">
        <p>
          This privacy policy describes how <strong>{BUSINESS_IDENTITY.legalName}</strong> (d/b/a <strong>{BUSINESS_IDENTITY.tradeName}</strong>, "we", "us", or "our") collects, uses, and shares information about you when you visit our website at <a href={BUSINESS_IDENTITY.websiteUrl} className="underline">{BUSINESS_IDENTITY.websiteUrl}</a> or sign up for our services.
        </p>
        <Contact />
      </Section>

      <Section title="2. Information we collect">
        <ul className="list-disc pl-6 space-y-1">
          <li><strong>Account information</strong> — name, email, phone number, company name, industry, website, password (hashed).</li>
          <li><strong>Usage information</strong> — pages visited, features used, conversations with the AI assistant, IP address, device and browser metadata.</li>
          <li><strong>Customer data you upload</strong> — leads, customer records, product catalog, knowledge base content, order history.</li>
          <li><strong>Cookies and similar technologies</strong> — used to keep you signed in, remember preferences, and analyse aggregate usage.</li>
          <li><strong>SMS consent metadata</strong> — when you opt in to text messages we store the consent timestamp, source URL, the exact consent text version, and a one-way hash of your IP address (we do not store the raw IP).</li>
        </ul>
      </Section>

      <Section title="3. How we use information">
        <ul className="list-disc pl-6 space-y-1">
          <li>Provide, operate, and maintain {BUSINESS_IDENTITY.tradeName} services.</li>
          <li>Authenticate your account and protect against fraud.</li>
          <li>Send you transactional messages (account verification, security alerts, customer support replies, order updates).</li>
          <li>Improve our products and respond to support requests.</li>
        </ul>
      </Section>

      <Section title="4. SMS / text message program" id="sms-program">
        <p className="font-semibold text-foreground">
          Mobile information will not be shared with third parties or affiliates for marketing or promotional purposes. Text messaging originator opt-in data and consent will not be shared with any third parties.
        </p>
        <p>
          When you opt in to SMS messages (for example, by checking the consent box on our registration form), we may send messages relating to:
        </p>
        <ul className="list-disc pl-6 space-y-1">
          <li>One-time password (OTP) verification</li>
          <li>Account security notifications</li>
          <li>Customer support updates</li>
          <li>Order confirmations</li>
          <li>Order status notifications</li>
          <li>Service-related alerts</li>
        </ul>
        <p>
          Message frequency varies. Message and data rates may apply. Reply <strong>STOP</strong> to opt out and <strong>HELP</strong> for assistance, or contact us at <a href={`mailto:${BUSINESS_IDENTITY.supportEmail}`} className="underline">{BUSINESS_IDENTITY.supportEmail}</a>.
        </p>
        <p className="text-sm">
          Consent is not a condition of purchase or use of {BUSINESS_IDENTITY.tradeName} services.
        </p>
        <p className="text-sm">
          Wireless carriers are not liable for delayed or undelivered messages.
        </p>
        <p className="text-xs text-muted-foreground">
          See our dedicated <Link to="/sms-program" className="underline">SMS Program page</Link> and <Link to="/privacy-policy" className="underline">Privacy Policy</Link> for the complete program description.
        </p>
      </Section>

      <Section title="5. How we share information">
        <p>We do not sell your personal information. We share information only with:</p>
        <ul className="list-disc pl-6 space-y-1">
          <li>Service providers that operate our platform (hosting, email delivery, payment processing, SMS delivery via Twilio).</li>
          <li>Cloud infrastructure and database providers that store and process platform data on our behalf, under contractual confidentiality and data-protection obligations.</li>
          <li>Connected commerce and point-of-sale platforms (for example, Clover) that a merchant using our services has chosen to connect, solely to enable the order, catalog, and payment functionality that merchant has configured.</li>
          <li>Analytics and product-support tools that help us operate, secure, and improve the platform.</li>
          <li>Law-enforcement or regulators when required by law.</li>
          <li>A successor entity in connection with a merger, acquisition, or sale.</li>
        </ul>
        <p className="text-sm">As noted in section 4, SMS consent data is never shared.</p>
      </Section>

      <Section title="6. Cookies">
        <p>We use cookies for authentication, session management, and analytics. You can disable cookies in your browser; doing so may impact functionality. We do not use cookies for cross-site advertising.</p>
      </Section>

      <Section title="7. Data retention">
        <p>We retain account and customer data for as long as your account is active. After account closure we retain data only as required by law (typically up to 7 years for billing records) and then delete it.</p>
      </Section>

      <Section title="8. Your rights">
        <p>You may request access to, correction of, or deletion of your personal information by emailing <a href={`mailto:${BUSINESS_IDENTITY.supportEmail}`} className="underline">{BUSINESS_IDENTITY.supportEmail}</a>. We respond within 30 days.</p>
      </Section>

      <Section title="9. Children">
        <p>{BUSINESS_IDENTITY.tradeName} is not directed to children under 13 and we do not knowingly collect information from them.</p>
      </Section>

      <Section title="10. Changes">
        <p>We may update this policy from time to time. Material changes will be posted with an updated effective date.</p>
      </Section>

      <Section title="11. Contact">
        <Contact />
      </Section>

      <Section title="12. Merchant data ownership">
        <p>
          Business customers ("merchants") who use {BUSINESS_IDENTITY.tradeName} retain ownership of the customer records, product catalogs, order history, and other business data they upload to or generate on our platform. We process that data on the merchant's behalf to provide our services and do not claim ownership of it. When a merchant connects a third-party business platform (such as a point-of-sale system), data exchanged through that connection remains subject to this policy and to the merchant's agreement with that third-party platform. {BUSINESS_IDENTITY.tradeName} does not use merchant-uploaded customer information to market directly to the merchant's customers.
        </p>
      </Section>

      <Section title="13. Security practices">
        <p>
          We use industry-standard safeguards to help protect the information we hold, including encryption of sensitive data, access controls limiting who can view account and integration credentials, and ongoing monitoring for unauthorized access. No method of storage or transmission is completely secure, and we cannot guarantee absolute security, but we work to continually evaluate and improve our safeguards.
        </p>
      </Section>

      <Section title="14. Connected business platforms">
        <p>
          With a merchant's explicit authorization, {BUSINESS_IDENTITY.tradeName} can connect to third-party business platforms — for example, point-of-sale providers such as Clover — using that platform's own authorization process (OAuth). The merchant controls which account is connected, what access is granted, and may disconnect the integration at any time through their account settings. We access only the data necessary to provide the specific connected functionality the merchant has enabled.
        </p>
      </Section>

      <Section title="15. International users">
        <p>
          {BUSINESS_IDENTITY.tradeName} is operated from the United States and is intended for use by businesses operating in the United States. If you access our services from outside the United States, your information will be transferred to and processed in the United States, where data protection laws may differ from those of your jurisdiction.
        </p>
      </Section>
    </PageShell>
  );
}

// ===================================================================
// /terms-of-service
// ===================================================================
export function TermsOfServicePage() {
  return (
    <PageShell testid="terms-of-service-page">
      <Badge variant="outline" className="mb-3"><FileText className="w-3 h-3 mr-1"/>Compliance</Badge>
      <h1 className="font-display text-4xl font-semibold mb-1" data-testid="terms-of-service-heading">Terms of Service</h1>
      <div className="text-sm text-muted-foreground mb-8">Last updated {updated}</div>

      <Section title="1. Acceptance">
        <p>By creating an account with <strong>{BUSINESS_IDENTITY.legalName}</strong> (d/b/a <strong>{BUSINESS_IDENTITY.tradeName}</strong>) you agree to these terms. If you do not agree, do not use the service.</p>
      </Section>

      <Section title="2. Platform usage">
        <p>{BUSINESS_IDENTITY.tradeName} provides an AI-powered customer engagement, lead management, retail order management, and messaging platform. You may use the platform only for lawful purposes and in accordance with these terms.</p>
      </Section>

      <Section title="3. User responsibilities">
        <ul className="list-disc pl-6 space-y-1">
          <li>Provide accurate registration information and keep it current.</li>
          <li>Keep your password confidential; you are responsible for activity under your account.</li>
          <li>Only upload content you have the right to use; do not upload illegal, harmful, or infringing content.</li>
          <li>Only send messages to recipients who have lawfully opted in. You are responsible for honouring opt-outs.</li>
          <li>Do not attempt to disrupt, reverse-engineer, or gain unauthorized access to the platform.</li>
        </ul>
      </Section>

      <Section title="4. Messaging disclosure">
        {/* PILOT-1B Feb 2026 — Twilio A2P 10DLC carrier-required
            disclosure block. Every clause below is mandatory under
            CTIA messaging principles + Twilio's A2P verifier
            checklist. Do NOT remove or paraphrase aggressively
            without legal review — Twilio's bot literally greps for
            these phrases. */}
        <p>{BUSINESS_IDENTITY.tradeName} may deliver SMS messages on your behalf via Twilio or another carrier. By using the messaging features you represent that you have collected proper consent from each recipient as required by the TCPA, CTIA guidelines, and your local law.</p>
        <p className="font-medium mt-3">{BUSINESS_IDENTITY.tradeName} SMS Program — terms applicable to every recipient:</p>
        <ul className="list-disc pl-6 space-y-1">
          <li><strong>Sender:</strong> Messages are sent by {BUSINESS_IDENTITY.legalName} (d/b/a {BUSINESS_IDENTITY.tradeName}).</li>
          <li><strong>Message types:</strong> One-Time Password (OTP) verification codes, account registration verification, password reset notifications, order confirmations, order status updates, customer support notifications, and service alerts. No marketing.</li>
          <li><strong>Message frequency:</strong> Message frequency varies — typically 1–6 transactional messages per active order. No recurring marketing cadence.</li>
          <li><strong>Cost:</strong> <em>Message and data rates may apply.</em> Check with your wireless carrier for plan-specific charges.</li>
          <li><strong>Opt-out:</strong> Reply <strong>STOP</strong> at any time to opt out of all {BUSINESS_IDENTITY.tradeName} SMS messages. After STOP you will receive one confirmation message and then no further SMS.</li>
          <li><strong>Help:</strong> Reply <strong>HELP</strong> for help, or contact <a href={`mailto:${BUSINESS_IDENTITY.supportEmail}`} className="underline">{BUSINESS_IDENTITY.supportEmail}</a>.</li>
          <li><strong>Consent is not a condition of purchase</strong> or of using {BUSINESS_IDENTITY.tradeName} services. You may place orders and use the platform without ever opting in to SMS — email is always an available alternative.</li>
          <li><strong>Carrier non-liability:</strong> Wireless carriers are not liable for delayed or undelivered messages.</li>
          <li><strong>Privacy:</strong> Your phone number and consent record are handled per our <Link to="/privacy-policy" className="underline">Privacy Policy</Link>. Text-messaging originator opt-in data and consent will not be shared with any third parties.</li>
        </ul>
        <p className="mt-3">For the full {BUSINESS_IDENTITY.tradeName} SMS Program description — including verbatim opt-in workflow, consent text versions, and contact information — see our <Link to="/sms-program" className="underline">SMS Program page</Link>.</p>
      </Section>

      <Section title="5. Account termination">
        <p>You may cancel your account at any time. We may suspend or terminate accounts that violate these terms, abuse the messaging system, or pose a security risk. We will provide notice where practical.</p>
      </Section>

      <Section title="6. Subscription and payment">
        <p>Paid plans are billed in advance and are non-refundable except where required by law. You authorise us to charge your selected payment method on a recurring basis until you cancel.</p>
      </Section>

      <Section title="7. Intellectual property">
        <p>Content you upload remains your property. You grant us a limited licence to process that content solely to provide the service to you. The {BUSINESS_IDENTITY.tradeName} platform, brand, and software remain our property.</p>
      </Section>

      <Section title="8. Limitation of liability">
        <p>To the maximum extent permitted by law, {BUSINESS_IDENTITY.legalName} is not liable for indirect, incidental, special, consequential, or punitive damages, or for lost profits or revenues, arising from your use of the service. Our total liability is capped at the amount you paid us in the 12 months preceding the claim.</p>
      </Section>

      <Section title="9. Governing law">
        <p>These terms are governed by the laws of the State of Texas, USA, without regard to its conflict-of-laws principles. Disputes will be resolved in the state or federal courts located in Harris County, Texas.</p>
      </Section>

      <Section title="10. Changes">
        <p>We may update these terms. Material changes will be posted with an updated effective date and, where appropriate, notified by email.</p>
      </Section>

      <Section title="11. Contact">
        <Contact />
      </Section>
    </PageShell>
  );
}

// ===================================================================
// /sms-program
// ===================================================================
export function SmsProgramPage() {
  return (
    <PageShell testid="sms-program-page">
      <Badge variant="outline" className="mb-3"><MessageCircleMore className="w-3 h-3 mr-1"/>SMS Program</Badge>
      <h1 className="font-display text-4xl font-semibold mb-1" data-testid="sms-program-heading">SMS / Text Message Program</h1>
      <div className="text-sm text-muted-foreground mb-8">{BUSINESS_IDENTITY.legalName} (d/b/a {BUSINESS_IDENTITY.tradeName}) · Last updated {updated}</div>

      <Section title="What messages do we send?">
        <p>When you opt in, <strong>{BUSINESS_IDENTITY.tradeName}</strong> may send SMS messages for:</p>
        <ul className="list-disc pl-6 space-y-1">
          <li>One-time password (OTP) verification codes</li>
          <li>Account security alerts</li>
          <li>Customer support notifications</li>
          <li>Order confirmations</li>
          <li>Order status updates</li>
          <li>Service-related alerts</li>
        </ul>
        <p className="text-sm text-muted-foreground">
          We do <strong>not</strong> send marketing, promotional, or appointment-reminder messages under this program.
        </p>
      </Section>

      <Section title="How do I opt in?">
        <p>You can opt in by checking the SMS consent box on the <Link to="/register" className="underline">registration page</Link>. The checkbox is <strong>not pre-selected</strong> — you must actively check it. By doing so you agree to receive the messages described above.</p>
        <p className="text-sm font-semibold text-foreground">
          Consent is not a condition of purchase or use of {BUSINESS_IDENTITY.tradeName} services.
        </p>
      </Section>

      <Section title="Message frequency and rates">
        <p>Message frequency varies based on your account activity (e.g. password resets, order updates). Message and data rates may apply. Check with your carrier for details.</p>
      </Section>

      <Section title="How do I opt out?">
        <ul className="list-disc pl-6 space-y-1">
          <li>Reply <strong>STOP</strong> to any message from us. You will receive a final confirmation that you have been unsubscribed and no further messages will be sent.</li>
          <li>To re-subscribe, send <strong>START</strong>, <strong>UNSTOP</strong>, or re-check the SMS consent box on the registration page.</li>
        </ul>
      </Section>

      <Section title="How do I get help?">
        <ul className="list-disc pl-6 space-y-1">
          <li>Reply <strong>HELP</strong> to any message for support information.</li>
          <li>Email <a href={`mailto:${BUSINESS_IDENTITY.supportEmail}`} className="underline">{BUSINESS_IDENTITY.supportEmail}</a></li>
          <li>Call <a href={`tel:${BUSINESS_IDENTITY.phoneE164}`} className="underline">{BUSINESS_IDENTITY.phoneDisplay}</a></li>
        </ul>
      </Section>

      <Section title="Privacy">
        <p>Mobile information will not be shared with third parties or affiliates for marketing or promotional purposes. Text messaging originator opt-in data and consent will not be shared with any third parties. See our <Link to="/privacy-policy" className="underline">Privacy Policy</Link> for full details.</p>
      </Section>

      <Section title="Carriers">
        <p>Carriers (including AT&amp;T, T-Mobile, Verizon, and others) are not liable for delayed or undelivered messages.</p>
      </Section>

      <Section title="Exact consent text (version pinned)">
        <Card className="p-4 bg-slate-50 border-slate-200 text-sm font-mono leading-relaxed" data-testid="sms-program-consent-text">
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Version {SMS_CONSENT_TEXT_VERSION}</div>
          {SMS_CONSENT_TEXT}
        </Card>
      </Section>

      <Section title="Contact">
        <Contact />
      </Section>
    </PageShell>
  );
}

// ===================================================================
// /contact
// ===================================================================
export function ContactPage() {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please fill in all fields.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/public/inbound-demo-lead", {
        full_name: form.name.trim(),
        email: form.email.trim(),
        company_name: "",
        phone: "",
        message: form.message.trim(),
        source: "contact_page",
      });
      if (data?.ok) {
        setSent(true);
        toast.success("Thanks! We'll be in touch shortly.");
      } else {
        toast.error("Couldn't send your message. Please email " + BUSINESS_IDENTITY.supportEmail + " directly.");
      }
    } catch (err) {
      const status = err?.response?.status;
      if (status === 429) {
        toast.error("Please wait a moment before submitting again.");
      } else {
        toast.error(
          err?.response?.data?.detail ||
          "Couldn't send your message. Please email " + BUSINESS_IDENTITY.supportEmail + " directly."
        );
      }
    } finally { setSubmitting(false); }
  };

  return (
    <PageShell testid="contact-page">
      <Badge variant="outline" className="mb-3"><Mail className="w-3 h-3 mr-1"/>Contact</Badge>
      <h1 className="font-display text-4xl font-semibold mb-3" data-testid="contact-heading">Contact Us</h1>
      <p className="text-sm text-muted-foreground mb-8">
        Questions about <strong>{BUSINESS_IDENTITY.tradeName}</strong>? Need help with an account, an order, or our SMS program? Get in touch — we read every message.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-10">
        <Card className="p-4">
          <Mail className="w-5 h-5 text-emerald-600 mb-2"/>
          <div className="text-xs text-muted-foreground">Email</div>
          <a href={`mailto:${BUSINESS_IDENTITY.supportEmail}`} className="font-medium text-sm underline" data-testid="contact-email">{BUSINESS_IDENTITY.supportEmail}</a>
        </Card>
        <Card className="p-4">
          <Phone className="w-5 h-5 text-emerald-600 mb-2"/>
          <div className="text-xs text-muted-foreground">Phone</div>
          <a href={`tel:${BUSINESS_IDENTITY.phoneE164}`} className="font-medium text-sm underline" data-testid="contact-phone">{BUSINESS_IDENTITY.phoneDisplay}</a>
        </Card>
        <Card className="p-4">
          <MapPin className="w-5 h-5 text-emerald-600 mb-2"/>
          <div className="text-xs text-muted-foreground">Address</div>
          <div className="text-sm" data-testid="contact-address">
            {BUSINESS_IDENTITY.addressLines.map((l, i) => <div key={i}>{l}</div>)}
          </div>
        </Card>
      </div>

      <Card className="p-5">
        <div className="text-base font-semibold mb-4">Send us a message</div>
        {sent ? (
          <div className="text-sm text-emerald-700" data-testid="contact-sent">Thanks for reaching out — we've received your message.</div>
        ) : (
          <form onSubmit={submit} className="space-y-3" data-testid="contact-form">
            <div>
              <label className="text-xs text-muted-foreground">Your name</label>
              <Input data-testid="contact-name" value={form.name} onChange={e => setForm({...form, name: e.target.value})} required/>
            </div>
            <div>
              <label className="text-xs text-muted-foreground">Email</label>
              <Input type="email" data-testid="contact-email-input" value={form.email} onChange={e => setForm({...form, email: e.target.value})} required/>
            </div>
            <div>
              <label className="text-xs text-muted-foreground">Message</label>
              <Textarea rows={5} data-testid="contact-message" value={form.message} onChange={e => setForm({...form, message: e.target.value})} required/>
            </div>
            <Button type="submit" disabled={submitting} data-testid="contact-submit">{submitting ? "Sending…" : "Send message"}</Button>
            <p className="text-[11px] text-muted-foreground">
              By submitting this form you agree to our <Link to="/privacy-policy" className="underline">Privacy Policy</Link>.
            </p>
          </form>
        )}
      </Card>
    </PageShell>
  );
}

// ===================================================================
// Shared helpers
// ===================================================================
const Section = ({ title, id, children }) => (
  <section className="mb-7" id={id}>
    <h2 className="font-display text-xl font-semibold mb-2">{title}</h2>
    <div className="prose prose-sm max-w-none text-muted-foreground space-y-2">{children}</div>
  </section>
);

const Contact = () => (
  <div className="not-prose rounded-md border bg-slate-50 px-4 py-3 text-sm">
    <div><strong>{BUSINESS_IDENTITY.legalName}</strong> (d/b/a {BUSINESS_IDENTITY.tradeName})</div>
    <div>{BUSINESS_IDENTITY.addressOneLine}</div>
    <div>Email: <a href={`mailto:${BUSINESS_IDENTITY.supportEmail}`} className="underline">{BUSINESS_IDENTITY.supportEmail}</a></div>
    <div>Phone: <a href={`tel:${BUSINESS_IDENTITY.phoneE164}`} className="underline">{BUSINESS_IDENTITY.phoneDisplay}</a></div>
  </div>
);
